import { useEffect } from 'react'
import { Link } from 'react-router-dom'
import { getBlogPostsByLang, blogListPath, blogPostPath } from '../../content/blog'
import {
  OG_IMAGE_URL,
  SITE_URL,
  upsertLink,
  upsertMeta,
  upsertJsonLd,
  removeJsonLd,
  breadcrumbJsonLd,
} from '../../seo/useSeo'
import { SUPPORTED_LANGUAGES, type SupportedLanguage } from '../../i18n'
import { MarketingHeader } from './MarketingHeader'
import { MarketingFooter } from './MarketingFooter'
import { useMarketingHeaderActions } from './useMarketingHeaderActions'

const BREADCRUMB_ID = 'blog-list-breadcrumb'

/** Chrome around the post list - not part of the post content objects since it's generic UI copy,
 *  same pattern as SeoLandingPage's FAQ_TITLE/RELATED_LINKS_TITLE. */
const COPY: Record<SupportedLanguage, { title: string; description: string; dateLocale: string }> = {
  de: {
    title: 'Blog | Roofora',
    description:
      'Hintergründe, Tipps und Neuigkeiten rund um Solaranlagen-Angebote, Datenschutz und den anonymen Vergleich von Handwerksbetrieben auf Roofora.',
    dateLocale: 'de-DE',
  },
  pl: {
    title: 'Blog o fotowoltaice | Roofora',
    description:
      'Wiedza, wskazówki i nowości na temat ofert instalacji fotowoltaicznych, ochrony danych i anonimowego porównywania firm rzemieślniczych na Roofora.',
    dateLocale: 'pl-PL',
  },
  es: {
    title: 'Blog de energía solar | Roofora',
    description:
      'Información, consejos y novedades sobre presupuestos de instalaciones solares, privacidad y la comparación anónima de empresas instaladoras en Roofora.',
    dateLocale: 'es-ES',
  },
}

/** Shares the landing page header/footer so it reads as part of the site rather than an isolated
 *  page - visitors landing here via search should be one click from the app. `lang` comes from the
 *  route (see App.tsx: /blog is DE, /pl/blog and /es/blog the translated indexes). */
export function BlogListPage({ lang = 'de' }: { lang?: SupportedLanguage }) {
  const headerActions = useMarketingHeaderActions()
  const copy = COPY[lang]
  const posts = getBlogPostsByLang(lang)

  useEffect(() => {
    document.documentElement.lang = lang
    const canonicalUrl = `${SITE_URL}${blogListPath(lang)}`

    document.title = copy.title
    upsertMeta('name', 'description', copy.description)
    upsertMeta('property', 'og:title', copy.title)
    upsertMeta('property', 'og:description', copy.description)
    upsertMeta('property', 'og:url', canonicalUrl)
    upsertMeta('property', 'og:image', OG_IMAGE_URL)
    upsertMeta('name', 'twitter:title', copy.title)
    upsertMeta('name', 'twitter:description', copy.description)
    upsertMeta('name', 'twitter:image', OG_IMAGE_URL)
    upsertLink('canonical', canonicalUrl)

    for (const supported of SUPPORTED_LANGUAGES) {
      upsertLink('alternate', `${SITE_URL}${blogListPath(supported)}`, supported)
    }
    upsertLink('alternate', `${SITE_URL}${blogListPath('de')}`, 'x-default')

    upsertJsonLd(
      BREADCRUMB_ID,
      breadcrumbJsonLd([
        { name: 'Roofora', url: `${SITE_URL}/${lang}` },
        { name: 'Blog', url: canonicalUrl },
      ]),
    )

    return () => removeJsonLd(BREADCRUMB_ID)
  }, [lang, copy])

  return (
    <>
      <MarketingHeader {...headerActions} />
      <div className="blog-page">
        <h1>Roofora Blog</h1>
        <p className="blog-list-intro">{copy.description}</p>
        <ul className="blog-list">
          {posts.map((post) => (
            <li key={post.slug} className="blog-list-item">
              <Link to={blogPostPath(post)}>
                <h2>{post.title}</h2>
              </Link>
              <time dateTime={post.publishedAt}>
                {new Date(post.publishedAt).toLocaleDateString(copy.dateLocale, {
                  day: '2-digit',
                  month: 'long',
                  year: 'numeric',
                })}
              </time>
              <p>{post.excerpt}</p>
            </li>
          ))}
        </ul>
      </div>
      <MarketingFooter />
    </>
  )
}
